import React from 'react';
import { string } from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import config from '../../config';
import { NamedLink, LanguagesSwitcher } from '../../components';
import { HeartIcon } from '../../icons';
import { getCurrentUserLocation } from '../../util/localStorage';
import { WEB_VIEW } from '../../util/ios';
import css from './Footer.css';
import BottomLinks from './BottomLinks';
import SocialMediaIcons from './SocialMediaIcons';
import { useCurrentEntityLocation } from '../../hooks/useCurrentEntityLocation';

const windowDefined = typeof window !== 'undefined';
const isNative = windowDefined && !!(window?.isNative || window[WEB_VIEW]);

const {
    listingTypeRider,
    initialSearchAddress,
    defaultCountry,
    maps: { supportedCountries },
} = config;

const Footer = ({ rootClassName, className }) => {
    const currentUserGeoData = useCurrentEntityLocation();
    const { address: geoAddress, bounds: geoBounds, origin: geoOrigin } = currentUserGeoData || {};

    const { country, countrySelected } = getCurrentUserLocation();
    const {
        code,
        defaultAddress: { origin: defaultOrigin },
    } =
        supportedCountries[countrySelected] ||
        supportedCountries[country] ||
        supportedCountries[defaultCountry];

    const { address: defaultAddress, bounds: defaultBounds } = initialSearchAddress[code];

    const getSearchParams = () => {
        if (geoAddress && geoBounds && geoOrigin) {
            const origin = `${geoOrigin?.lat},${geoOrigin?.lng}`;
            const bounds = `${geoBounds?.ne?.lat},${geoBounds?.ne?.lng},${geoBounds?.sw?.lat},${geoBounds?.sw?.lng}`;

            return `?address=${geoAddress}&bounds=${bounds}&origin=${origin}&listingType=${listingTypeRider}`;
        }
        return `?address=${defaultAddress}&bounds=${defaultBounds}&origin=${defaultOrigin}&listingType=${listingTypeRider}`;
    };

    const searchParams = getSearchParams();

    return (
        <footer className={classNames(rootClassName || css.root, className)}>
            <div className={css.content}>
                <section className={css.sectionLinksMain}>
                    <div className={css.footerLinkBlock}>
                        <p>Für Besitzer:innen</p>
                        <ul className={css.list}>
                            <li className={css.listItem}>
                                <NamedLink name="NewListingPage" className={css.link}>
                                    Pferd hinzufügen
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="FindRiderPage" className={css.link}>
                                    Vorteile als Besitzer:in
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink
                                    name="SearchPage"
                                    to={{
                                        search: `${searchParams}&pub_activities=has_any%3AAusreiten%2CTraining%2CTurniere`,
                                    }}
                                    className={css.link}
                                >
                                    Reiter:in finden
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink
                                    name="SearchPage"
                                    to={{
                                        search: `${searchParams}&pub_activities=has_any%3ASpazieren`,
                                    }}
                                    className={css.link}
                                >
                                    Spazierer:in finden
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink
                                    name="SearchPage"
                                    to={{
                                        search: `${searchParams}&pub_activities=has_any%3APflegen%2CBodenarbeit`,
                                    }}
                                    className={css.link}
                                >
                                    Pfleger:in finden
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="ReferralPage" className={css.link}>
                                    Freunde einladen
                                </NamedLink>
                            </li>
                        </ul>
                    </div>
                    <div className={css.footerLinkBlock}>
                        <p>Für Pferdemenschen</p>
                        <ul className={css.list}>
                            <li className={css.listItem}>
                                <NamedLink name="SignupRider" className={css.link}>
                                    Kostenlos Registrieren
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="FindHorsePage" className={css.link}>
                                    Vorteile als Pferdemensch
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="ReitbeteiligungFindenPage" className={css.link}>
                                    Reitbeteiligung finden
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="SpazierbeteiligungFindenPage" className={css.link}>
                                    Spazierbeteiligung finden
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="PflegebeteiligungFindenPage" className={css.link}>
                                    Pflegebeteiligung finden
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="ReferralPage" className={css.link}>
                                    Freunde einladen
                                </NamedLink>
                            </li>
                        </ul>
                    </div>
                    <div className={css.footerLinkBlock}>
                        <p>HorseDeal</p>
                        <ul className={css.list}>
                            <li className={css.listItem}>
                                <NamedLink name="AboutUs" className={css.link}>
                                    Über uns
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <a className={css.link} href="/community">
                                    Community
                                </a>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="BecomePartnerPage" className={css.link}>
                                    Für Unternehmen
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="ForClubs" className={css.link}>
                                    Für Vereine
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="CareerPage" className={css.link}>
                                    Karriere
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <a className={css.link} href="/kontakt">
                                    Kontakt
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div className={css.footerLinkBlock}>
                        <p>Ressourcen</p>
                        <ul className={css.list}>
                            <li className={css.listItem}>
                                <NamedLink name="MatchingPage" className={css.link}>
                                    Matching-Algorithmus
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <a className={css.link} href="/support">
                                    Häufige Fragen
                                </a>
                            </li>
                            <li className={css.listItem}>
                                <NamedLink name="KodexPage" className={css.link}>
                                    Unser Kodex
                                </NamedLink>
                            </li>
                            <li className={css.listItem}>
                                <a className={css.link} href="/flip-and-win">
                                    Flip & Win
                                </a>
                            </li>
                            <li className={css.listItem}>
                                <a className={css.link} href="/presse">
                                    Presse
                                </a>
                            </li>
                            <li className={css.listItem}>
                                <a className={css.link} href="/blog">
                                    Blog
                                </a>
                            </li>
                        </ul>
                    </div>
                </section>

                <section className={css.sectionSocialStores}>
                    {isNative ? null : (
                        <div className={css.socialStores}>
                            <a
                                href="https://support.horsedeal.com/hc/de/articles/17567786473618-Bietet-HorseDeal-eine-Mobile-App-an"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={css.storelink}
                            >
                                <img
                                    src="https://www.horsedeal.com/static/icons/google-playstore-badge.svg"
                                    className={css.appPlayStoreIcon}
                                    alt="Playstore Badge"
                                />
                            </a>
                            <a
                                href="https://support.horsedeal.com/hc/de/articles/17567786473618-Bietet-HorseDeal-eine-Mobile-App-an"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    src="https://www.horsedeal.com/static/icons/apple-appstore-badge.svg"
                                    className={css.appPlayStoreIcon}
                                    alt="Appstore Badge"
                                />
                            </a>
                        </div>
                    )}

                    <LanguagesSwitcher rootClassName={css.langSwitcher} />
                </section>

                <section className={css.mob}>
                    <ul className={css.bottomLinksMobile}>
                        <BottomLinks />
                    </ul>
                </section>

                <aside className={css.sectionSideNote}>
                    Erstellt mit
                    <HeartIcon />
                    für dich.
                </aside>

                <section className={css.sectionSocialLinks}>
                    <div className={css.desktop}>
                        <ul className={css.list}>
                            <li key="copyright" className={classNames(css.listItem, css.copyright)}>
                                © 2026 HorseDeal AG
                            </li>
                            <BottomLinks />
                        </ul>
                    </div>
                    <div className={css.socialLinks}>
                        <SocialMediaIcons />
                    </div>
                </section>

                <aside className={classNames(css.mob, css.mobileCopyright)}>
                    © 2026 HorseDeal AG
                </aside>
            </div>
        </footer>
    );
};

Footer.propTypes = {
    rootClassName: string,
    className: string,
    intl: intlShape.isRequired,
};

export default injectIntl(Footer);
